// Entscheidet beim Start, ob MSAL geladen werden muss. Läuft eifrig und darf deshalb
// @azure/msal-* nicht berühren - weder als Wert noch als Typ mit Laufzeitanteil.
//
// Drei Fälle verlangen MSAL:
//
//   1. Es liegen Konto- oder Tokeneinträge im localStorage. Deren Schlüssel tragen die
//      Schemaversion: msal-browser 4 schrieb "msal.1.", msal-browser 5 schreibt "msal.3." und
//      "msal.3|". Deshalb das versionsunabhängige Muster - ein festes Präfix lässt die
//      Erkennung nach dem nächsten MSAL-Sprung stillschweigend ins Leere laufen. Ausgeschlossen
//      sind damit "msal.version" und "msal.ade.loginHint", die auch ohne Anmeldung existieren.
//      Denselben Test macht das SEO-Inline-Script in environment-script-template.html.
//   2. Die Adresse trägt eine Antwort des Anmeldedienstes (Code, State oder Fehler) - dann
//      muss handleRedirectPromise sie verarbeiten.
//   3. Der Nutzer hat gerade Anmelden oder Registrieren geklickt. Die No-Auth-Fassung setzt
//      dafür eine Markierung und lädt die Seite neu; beim nächsten Start greift dieser Fall.
//      Ein Neuladen ist dort unschädlich, weil die Anmeldung ohnehin auf b2clogin.com umleitet.
//   4. Es liegt ein gespeicherter Anmeldehinweis vor. Der wird ausschließlich im angemeldeten
//      Zustand geschrieben (persistLoginHint() hängt an isLoggedIn()) und bei LOGOUT_SUCCESS
//      wieder gelöscht - seine Anwesenheit heißt also: in diesem Browser bestand eine Sitzung,
//      und trySilentSsoRestore() soll sie über das KMSI-Cookie von B2C still wiederherstellen.
//      Das braucht echtes MSAL: die No-Auth-Fassung kann ssoSilent nicht ausführen.

export const LOGIN_PENDING_KEY = "ade.auth.startLogin";

/**
 * Wird gesetzt, wenn eine Route eine Anmeldung verlangt, MSAL aber nicht geladen ist. Die Seite
 * lädt dann neu, greift in den MSAL-Zweig, und der echte Routenschutz entscheidet erneut -
 * das Verhalten ist danach identisch zu vorher, nur um einen Neuladevorgang verzögert.
 */
export const AUTH_REQUIRED_KEY = "ade.auth.routeNeedsAuth";

/**
 * Speicherort des Anmeldehinweises für den stillen SSO-Wiederaufbau. Liegt hier und nicht in
 * MsalB2CService, weil die Startentscheidung ihn braucht und dieser Helfer nichts aus dem
 * MSAL-Zweig importieren darf; MsalB2CService bezieht die Konstante von hier.
 */
export const LOGIN_HINT_KEY = "msal.ade.loginHint";

const MSAL_CACHE_KEY_PATTERN = /^msal\.\d+[.|]/;
const REDIRECT_RESPONSE_PATTERN = /[?#].*\b(code|state|error)=/;
const AUTH_PAYLOAD_PATTERN = /[?#].*\b(code|error)=/;
const AUTH_STATE_PATTERN = /[?#].*\bstate=/;

/**
 * Meldet, ob dieses Dokument die Antwort des Anmeldedienstes nur an das Hauptfenster
 * weiterzureichen hat, statt die Anwendung zu starten.
 *
 * msal-browser 5 liefert die Antwort aus dem stillen iframe und aus dem Popup nicht mehr im
 * Dokument selbst ab, sondern über eine Redirect-Bridge: das Hauptfenster wartet auf
 * `BroadcastChannel(libraryState.id)`, und an der Redirect-Adresse muss
 * `broadcastResponseToMainFrame()` aus `@azure/msal-browser/redirect-bridge` laufen. Beim
 * stillen SSO ist diese Adresse ein verstecktes iframe (`className = "msalSilentIframe"`).
 *
 * Ohne diese Weiche bootet dort die komplette Anwendung. Ihr eigener
 * `handleRedirectPromise`-Aufruf verbraucht den Hash, kann ihn aber nicht verwerten - der State
 * trägt `interactionType: "silent"` und beschreibt damit keine Redirect-Anmeldung - und das
 * Hauptfenster bekommt nie eine Nachricht. Gemessen: B2C antwortet auf `prompt=none` mit 302,
 * aber der Token-Endpunkt wird nie aufgerufen. Sichtbar war das als dauerhaft hängender
 * Ladezustand im Kontomenü nach einem Browser-Neustart - denn dann läuft der Wiederaufbau über
 * das KMSI-Cookie, und der geht ausschließlich durch dieses iframe.
 *
 * Nur Nicht-Hauptfenster werden abgezweigt: der Rücksprung einer Redirect-Anmeldung im
 * Hauptfenster wird weiter von `handleRedirectPromise` verarbeitet. Und die Anwendung läuft
 * berechtigt in Partner-iframes - dort steht keine Antwort in der Adresse, die Bedingung greift
 * also nicht.
 */
export const isAuthResponseBridgeTarget = (): boolean => {
	if (typeof window === "undefined") {
		return false;
	}
	const isSecondaryWindow = window.parent !== window || window.opener !== null;
	return isSecondaryWindow
		&& AUTH_PAYLOAD_PATTERN.test(window.location.href)
		&& AUTH_STATE_PATTERN.test(window.location.href);
};

export const hasCachedMsalEntries = (): boolean =>
	typeof localStorage !== "undefined" && Object.keys(localStorage).some(key => MSAL_CACHE_KEY_PATTERN.test(key));

export const hasRedirectResponse = (): boolean =>
	typeof window !== "undefined" && REDIRECT_RESPONSE_PATTERN.test(window.location.href);

export const hasPendingLogin = (): boolean =>
	typeof sessionStorage !== "undefined" &&
	(sessionStorage.getItem(LOGIN_PENDING_KEY) !== null || sessionStorage.getItem(AUTH_REQUIRED_KEY) !== null);

/**
 * Ein gespeicherter Anmeldehinweis bedeutet: hier bestand eine Sitzung, die über ssoSilent
 * wiederherstellbar sein kann. Ohne diesen Fall bliebe die No-Auth-Fassung aktiv, deren ssoSilent
 * wirft - trySilentSsoRestore() meldet dann trotzdem `true`, subscribeState() überspringt daraufhin
 * handleLogin(), und die Initialisierung bleibt stehen (kein refreshMenu, kein initWatchlist,
 * authStateSignal nie `loaded`). Sichtbar war das als "die Anmeldung wird nicht ausgelöst, erst ein
 * zweiter Klick hilft" - der Klick führt über die Markierung in den MSAL-Zweig.
 */
export const hasPersistedLoginHint = (): boolean =>
	typeof localStorage !== "undefined" && localStorage.getItem(LOGIN_HINT_KEY) !== null;

/**
 * Meldet unfertige MSAL-Arbeit im temporären Cache. Der liegt in der sessionStorage, und die
 * Schlüssel heißen `msal.<clientId>.<name>` bzw. `msal.interaction.status`.
 *
 * Das ist der Fall, der auf einer Katalogseite scheiterte und auf der Startseite nicht. Die
 * `redirect_uri` ist die Origin, der Rücksprung von B2C landet also immer auf `/`. Stand die
 * Anmeldung dort an, ist Ursprungsadresse gleich Rücksprungadresse und `handleRedirectPromise`
 * verarbeitet die Antwort sofort. Von jeder anderen Route aus sind sie verschieden, und dann
 * legt msal-browser die Antwort unter `urlHash` in den temporären Cache und navigiert erst auf
 * die Ursprungsadresse - mit dem Kommentar „The start page is expected to also call
 * handleRedirectPromise which will process the hash in one of the checks above."
 *
 * Auf dieser Ursprungsadresse steht dann nichts mehr, woran die Erkennung MSAL festmachen
 * könnte: keine Antwort in der Adresse, noch keine Tokens im localStorage, die Markierung der
 * angestoßenen Anmeldung ist verbraucht, und einen Anmeldehinweis gibt es bei der ersten
 * Anmeldung noch nicht. Ohne diese Prüfung fiel der Start in den No-Auth-Zweig, der
 * zwischengespeicherte Hash wurde nie verarbeitet, und die Anmeldung ging still verloren.
 */
export const hasPendingMsalInteraction = (): boolean =>
	typeof sessionStorage !== "undefined" && Object.keys(sessionStorage).some(key => key.startsWith("msal."));

export const isAuthenticationNeededAtStartup = (): boolean =>
	hasCachedMsalEntries()
	|| hasRedirectResponse()
	|| hasPendingLogin()
	|| hasPersistedLoginHint()
	|| hasPendingMsalInteraction();

/**
 * Entfernt ein `state`, das vom Abmelden in der Adresse zurückbleibt.
 *
 * msal-browser 5 setzt beim Abmelden unbedingt ein `state` auf die End-Session-Anfrage
 * ("Redirect bridge requires state param to work properly"), Azure AD B2C spiegelt es an
 * `post_logout_redirect_uri` zurück, und niemand räumt es wieder weg: der SIGNOUT-Zweig in
 * `handleRedirectPromise` liest nur das Fortschritts-Flag aus dem Speicher und lässt die Adresse
 * unberührt. msal-browser 4 kannte die Zuweisung nicht - daher ist der Parameter neu.
 *
 * Nur die Query wird angefasst, nie das Fragment: Anmeldeantworten kommen mit
 * `response_mode=fragment` als `#code=…&state=…`, das Abmelden antwortet in der Query. Zusätzlich
 * wird abgebrochen, wenn `code` oder `error` daneben stehen - dann wäre es doch eine
 * Anmeldeantwort, und MSAL muss sie unverändert vorfinden.
 *
 * Reihenfolge: erst nach `isAuthenticationNeededAtStartup()` aufrufen. Wird der Parameter vorher
 * entfernt, meldet die Erkennung keinen Bedarf, MSAL wird nicht geladen, und
 * `msal.interaction.status` bleibt mit Typ SIGNOUT in der sessionStorage stehen. Die nächste
 * Anmeldung ruft `setInteractionInProgress(true, SIGNIN)` ohne `overrideInteractionInProgress`
 * und läuft damit in `interaction_in_progress`.
 */
export const removeLogoutStateFromUrl = (): void => {
	if (typeof window === "undefined" || typeof window.history?.replaceState !== "function") {
		return;
	}
	const url = new URL(window.location.href);
	if (!url.searchParams.has("state") || url.searchParams.has("code") || url.searchParams.has("error")) {
		return;
	}
	url.searchParams.delete("state");
	const query = url.searchParams.toString();
	window.history.replaceState(window.history.state, "", `${url.pathname}${query.length > 0 ? `?${query}` : ""}${url.hash}`);
};
